/* ═══════════════════════════════════════════════════════════════════════════
   FOOD SERVICE & MENU — IMAGE DATA
   Premium visual experience for banquet booking
   ═══════════════════════════════════════════════════════════════════════════ */

/* ── Food Service Images (16:9 hero images) ── */
export const FOOD_SERVICE_IMAGES: Record<string, { image: string; description: string; badge?: string }> = {
  "buffet": {
    image: "https://images.unsplash.com/photo-1555244162-803834f70033?w=900&h=500&fit=crop",
    description: "Self-serve premium dining with an elegant spread of curated dishes",
    badge: "Popular",
  },
  "sit-down": {
    image: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=900&h=500&fit=crop",
    description: "Full table service with plated courses for an exquisite dining experience",
  },
  "plated": {
    image: "https://images.unsplash.com/photo-1544025162-d76694265947?w=900&h=500&fit=crop",
    description: "Individually crafted plates presented with artistic flair",
    badge: "Chef Recommended",
  },
  "family style": {
    image: "https://images.unsplash.com/photo-1547573854-74d2a71d0826?w=900&h=500&fit=crop",
    description: "Shared platters for a warm, communal dining experience",
  },
  "catering": {
    image: "https://images.unsplash.com/photo-1555244162-803834f70033?w=900&h=500&fit=crop",
    description: "Full-service catering tailored to your event",
  },
  "food station": {
    image: "https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=900&h=500&fit=crop",
    description: "Interactive live cooking stations for an engaging culinary journey",
    badge: "Popular",
  },
};
const DEFAULT_SERVICE_IMG = {
  image: "https://images.unsplash.com/photo-1555244162-803834f70033?w=900&h=500&fit=crop",
  description: "Premium dining service for your event",
};

export const getFoodServiceData = (name: string) => {
  if (!name) return null;
  const lower = name.toLowerCase();
  for (const [key, val] of Object.entries(FOOD_SERVICE_IMAGES)) {
    if (lower.includes(key)) return val;
  }
  return { ...DEFAULT_SERVICE_IMG, description: `${name} — curated dining for your event` };
};

/* ── Food Service Gallery Items (setups, styles, presentations) ── */
export type MediaType = "image" | "video";

export interface ServiceGalleryItem {
  image: string;
  title: string;
  tag?: "Popular" | "Luxury" | "Standard" | "Premium" | "Recommended";
  mediaType?: MediaType; // explicit: "image" (default) or "video"
  video?: string;        // MP4 URL — only used when mediaType === "video"
}

export const FOOD_SERVICE_GALLERY: Record<string, ServiceGalleryItem[]> = {
  "buffet": [
    { image: "https://images.unsplash.com/photo-1555244162-803834f70033?w=700&h=500&fit=crop", title: "Classic Buffet Spread", tag: "Popular" },
    { image: "https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=700&h=500&fit=crop", title: "Premium Buffet Setup", tag: "Luxury" },
    { image: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=700&h=500&fit=crop", title: "Wedding Buffet Style", tag: "Recommended" },
    { image: "https://images.unsplash.com/photo-1547573854-74d2a71d0826?w=700&h=500&fit=crop", title: "Corporate Buffet Layout", tag: "Standard" },
    { image: "https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=700&h=500&fit=crop", title: "Outdoor Buffet Station" },
    { image: "https://images.unsplash.com/photo-1432139555190-58524dae6a55?w=700&h=500&fit=crop", title: "Gala Buffet Presentation", tag: "Premium" },
  ],
  "sit-down": [
    { image: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=700&h=500&fit=crop", title: "Elegant Table Setting", tag: "Luxury" },
    { image: "https://images.unsplash.com/photo-1519167758481-83f550bb49b3?w=700&h=500&fit=crop", title: "Formal Dining Hall", tag: "Popular" },
    { image: "https://images.unsplash.com/photo-1544025162-d76694265947?w=700&h=500&fit=crop", title: "Fine Dining Experience", tag: "Premium" },
    { image: "https://images.unsplash.com/photo-1478146059778-26a4c1fd8f31?w=700&h=500&fit=crop", title: "Wedding Sit-down Setup", tag: "Recommended" },
    { image: "https://images.unsplash.com/photo-1432139555190-58524dae6a55?w=700&h=500&fit=crop", title: "Corporate Dinner" },
  ],
  "plated": [
    { image: "https://images.unsplash.com/photo-1544025162-d76694265947?w=700&h=500&fit=crop", title: "Chef's Plated Course", tag: "Luxury" },
    { image: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=700&h=500&fit=crop", title: "Artisan Plating Style", tag: "Premium" },
    { image: "https://images.unsplash.com/photo-1432139555190-58524dae6a55?w=700&h=500&fit=crop", title: "Signature Main Course", tag: "Popular" },
    { image: "https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=700&h=500&fit=crop", title: "Multi-Course Presentation" },
    { image: "https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=700&h=500&fit=crop", title: "Dessert Plating" },
  ],
  "family style": [
    { image: "https://images.unsplash.com/photo-1547573854-74d2a71d0826?w=700&h=500&fit=crop", title: "Family Feast Setup", tag: "Popular" },
    { image: "https://images.unsplash.com/photo-1555244162-803834f70033?w=700&h=500&fit=crop", title: "Shared Platter Service", tag: "Recommended" },
    { image: "https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=700&h=500&fit=crop", title: "Communal Dining" },
    { image: "https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=700&h=500&fit=crop", title: "Heritage Style Spread", tag: "Premium" },
  ],
  "catering": [
    { image: "https://images.unsplash.com/photo-1555244162-803834f70033?w=700&h=500&fit=crop", title: "Full-Service Catering", tag: "Popular" },
    { image: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=700&h=500&fit=crop", title: "Premium Event Catering", tag: "Luxury" },
    { image: "https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=700&h=500&fit=crop", title: "Outdoor Catering Setup" },
    { image: "https://images.unsplash.com/photo-1547573854-74d2a71d0826?w=700&h=500&fit=crop", title: "Corporate Catering", tag: "Standard" },
  ],
  "food station": [
    { image: "https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=700&h=500&fit=crop", title: "Live Cooking Station", tag: "Popular" },
    { image: "https://images.unsplash.com/photo-1555244162-803834f70033?w=700&h=500&fit=crop", title: "Interactive Food Bar", tag: "Luxury" },
    { image: "https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=700&h=500&fit=crop", title: "Carving Station", tag: "Premium" },
    { image: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=700&h=500&fit=crop", title: "Pasta Live Station" },
    { image: "https://images.unsplash.com/photo-1432139555190-58524dae6a55?w=700&h=500&fit=crop", title: "Sushi Station", tag: "Recommended" },
  ],
};

export const getFoodServiceGallery = (name: string): ServiceGalleryItem[] => {
  if (!name) return [];
  const lower = name.toLowerCase();
  for (const [key, items] of Object.entries(FOOD_SERVICE_GALLERY)) {
    if (lower.includes(key)) return items;
  }
  return FOOD_SERVICE_GALLERY["buffet"] || [];
};

/* ── Food Service Dropdown Thumbnails ── */
export const FOOD_SERVICE_THUMBS: Record<string, string> = {
  "buffet": "https://images.unsplash.com/photo-1555244162-803834f70033?w=80&h=60&fit=crop",
  "sit-down": "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=80&h=60&fit=crop",
  "plated": "https://images.unsplash.com/photo-1544025162-d76694265947?w=80&h=60&fit=crop",
  "family style": "https://images.unsplash.com/photo-1547573854-74d2a71d0826?w=80&h=60&fit=crop",
  "catering": "https://images.unsplash.com/photo-1555244162-803834f70033?w=80&h=60&fit=crop",
  "food station": "https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=80&h=60&fit=crop",
};

export const getFoodServiceThumb = (name: string): string | null => {
  if (!name) return null;
  const lower = name.toLowerCase();
  for (const [key, url] of Object.entries(FOOD_SERVICE_THUMBS)) {
    if (lower.includes(key)) return url;
  }
  return "https://images.unsplash.com/photo-1555244162-803834f70033?w=80&h=60&fit=crop";
};

/* ── Menu Option Images & Dish Galleries ── */
export interface DishItem {
  name: string;
  image: string;
  category: "Veg" | "Non-Veg" | "Desserts" | "Appetizer" | "Beverage";
  chefPick?: boolean;
}

export const MENU_HERO_IMAGES: Record<string, string> = {
  "south indian": "https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=600&h=400&fit=crop",
  "north indian": "https://images.unsplash.com/photo-1596797038530-2c107229654b?w=600&h=400&fit=crop",
  "sri lankan": "https://images.unsplash.com/photo-1625398407796-82650a8c135f?w=600&h=400&fit=crop",
  "tamil": "https://images.unsplash.com/photo-1630383249896-424e482df921?w=600&h=400&fit=crop",
  "western": "https://images.unsplash.com/photo-1544025162-d76694265947?w=600&h=400&fit=crop",
  "chinese": "https://images.unsplash.com/photo-1563245372-f21724e3856d?w=600&h=400&fit=crop",
  "thai": "https://images.unsplash.com/photo-1562565652-a0d8f0c59eb4?w=600&h=400&fit=crop",
  "caribbean": "https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=600&h=400&fit=crop",
  "mediterranean": "https://images.unsplash.com/photo-1540914124281-342587941389?w=600&h=400&fit=crop",
  "afghan": "https://images.unsplash.com/photo-1540914124281-342587941389?w=600&h=400&fit=crop",
  "pakistani": "https://images.unsplash.com/photo-1540914124281-342587941389?w=600&h=400&fit=crop",
  "vegetarian": "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=600&h=400&fit=crop",
  "international": "https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=600&h=400&fit=crop",
};

export const getMenuHeroImage = (name: string): string => {
  if (!name) return "https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=600&h=400&fit=crop";
  const lower = name.toLowerCase();
  for (const [key, url] of Object.entries(MENU_HERO_IMAGES)) {
    if (lower.includes(key)) return url;
  }
  return "https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=600&h=400&fit=crop";
};

export const getMenuCollageImages = (name: string): string[] => {
  if (!name) return [];
  const lower = name.toLowerCase();
  const dishes = MENU_DISHES[lower] || findMatchingDishes(lower);
  if (dishes.length > 0) return dishes.slice(0, 4).map((d) => d.image);
  return [
    "https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=300&h=300&fit=crop",
    "https://images.unsplash.com/photo-1555244162-803834f70033?w=300&h=300&fit=crop",
    "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=300&h=300&fit=crop",
    "https://images.unsplash.com/photo-1544025162-d76694265947?w=300&h=300&fit=crop",
  ];
};

/* ── Dish Galleries per Menu ── */
export const MENU_DISHES: Record<string, DishItem[]> = {
  "south indian": [
    { name: "Masala Dosa", image: "https://images.unsplash.com/photo-1630383249896-424e482df921?w=400&h=400&fit=crop", category: "Veg", chefPick: true },
    { name: "Idli Sambar", image: "https://images.unsplash.com/photo-1589301760014-d929f3979dbc?w=400&h=400&fit=crop", category: "Veg" },
    { name: "Chicken Chettinad", image: "https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=400&h=400&fit=crop", category: "Non-Veg", chefPick: true },
    { name: "Vada", image: "https://images.unsplash.com/photo-1567188040759-fb8a883dc6d8?w=400&h=400&fit=crop", category: "Veg" },
    { name: "Biryani", image: "https://images.unsplash.com/photo-1596797038530-2c107229654b?w=400&h=400&fit=crop", category: "Non-Veg", chefPick: true },
    { name: "Rasam", image: "https://images.unsplash.com/photo-1625398407796-82650a8c135f?w=400&h=400&fit=crop", category: "Veg" },
    { name: "Payasam", image: "https://images.unsplash.com/photo-1551024709-8f23befc6f87?w=400&h=400&fit=crop", category: "Desserts" },
    { name: "Uttapam", image: "https://images.unsplash.com/photo-1567188040759-fb8a883dc6d8?w=400&h=400&fit=crop", category: "Veg" },
  ],
  "north indian": [
    { name: "Butter Chicken", image: "https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=400&h=400&fit=crop", category: "Non-Veg", chefPick: true },
    { name: "Paneer Tikka", image: "https://images.unsplash.com/photo-1567188040759-fb8a883dc6d8?w=400&h=400&fit=crop", category: "Veg", chefPick: true },
    { name: "Naan Bread", image: "https://images.unsplash.com/photo-1596797038530-2c107229654b?w=400&h=400&fit=crop", category: "Veg" },
    { name: "Dal Makhani", image: "https://images.unsplash.com/photo-1625398407796-82650a8c135f?w=400&h=400&fit=crop", category: "Veg" },
    { name: "Tandoori Chicken", image: "https://images.unsplash.com/photo-1598515214211-89d3c73ae83b?w=400&h=400&fit=crop", category: "Non-Veg" },
    { name: "Gulab Jamun", image: "https://images.unsplash.com/photo-1551024709-8f23befc6f87?w=400&h=400&fit=crop", category: "Desserts", chefPick: true },
  ],
  "western": [
    { name: "Grilled Steak", image: "https://images.unsplash.com/photo-1544025162-d76694265947?w=400&h=400&fit=crop", category: "Non-Veg", chefPick: true },
    { name: "Caesar Salad", image: "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=400&h=400&fit=crop", category: "Veg" },
    { name: "Roast Chicken", image: "https://images.unsplash.com/photo-1598515214211-89d3c73ae83b?w=400&h=400&fit=crop", category: "Non-Veg" },
    { name: "Pasta Primavera", image: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=400&h=400&fit=crop", category: "Veg", chefPick: true },
    { name: "Chocolate Mousse", image: "https://images.unsplash.com/photo-1551024709-8f23befc6f87?w=400&h=400&fit=crop", category: "Desserts" },
    { name: "Lobster Tail", image: "https://images.unsplash.com/photo-1432139555190-58524dae6a55?w=400&h=400&fit=crop", category: "Non-Veg", chefPick: true },
  ],
  "chinese": [
    { name: "Dim Sum", image: "https://images.unsplash.com/photo-1563245372-f21724e3856d?w=400&h=400&fit=crop", category: "Appetizer", chefPick: true },
    { name: "Kung Pao Chicken", image: "https://images.unsplash.com/photo-1526318896980-cf78c088247c?w=400&h=400&fit=crop", category: "Non-Veg" },
    { name: "Fried Rice", image: "https://images.unsplash.com/photo-1585032226651-759b368d7246?w=400&h=400&fit=crop", category: "Veg" },
    { name: "Spring Rolls", image: "https://images.unsplash.com/photo-1563245372-f21724e3856d?w=400&h=400&fit=crop", category: "Appetizer" },
    { name: "Sweet & Sour Pork", image: "https://images.unsplash.com/photo-1526318896980-cf78c088247c?w=400&h=400&fit=crop", category: "Non-Veg", chefPick: true },
    { name: "Mango Pudding", image: "https://images.unsplash.com/photo-1551024709-8f23befc6f87?w=400&h=400&fit=crop", category: "Desserts" },
  ],
  "thai": [
    { name: "Pad Thai", image: "https://images.unsplash.com/photo-1562565652-a0d8f0c59eb4?w=400&h=400&fit=crop", category: "Non-Veg", chefPick: true },
    { name: "Green Curry", image: "https://images.unsplash.com/photo-1559314809-0d155014e29e?w=400&h=400&fit=crop", category: "Veg" },
    { name: "Tom Yum Soup", image: "https://images.unsplash.com/photo-1569562211093-4ed0d0758f12?w=400&h=400&fit=crop", category: "Appetizer", chefPick: true },
    { name: "Mango Sticky Rice", image: "https://images.unsplash.com/photo-1551024709-8f23befc6f87?w=400&h=400&fit=crop", category: "Desserts" },
    { name: "Satay Skewers", image: "https://images.unsplash.com/photo-1569562211093-4ed0d0758f12?w=400&h=400&fit=crop", category: "Appetizer" },
  ],
  "sri lankan": [
    { name: "Rice & Curry", image: "https://images.unsplash.com/photo-1625398407796-82650a8c135f?w=400&h=400&fit=crop", category: "Veg", chefPick: true },
    { name: "Kottu Roti", image: "https://images.unsplash.com/photo-1567188040759-fb8a883dc6d8?w=400&h=400&fit=crop", category: "Non-Veg" },
    { name: "Fish Ambul Thiyal", image: "https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=400&h=400&fit=crop", category: "Non-Veg", chefPick: true },
    { name: "String Hoppers", image: "https://images.unsplash.com/photo-1589301760014-d929f3979dbc?w=400&h=400&fit=crop", category: "Veg" },
    { name: "Watalappam", image: "https://images.unsplash.com/photo-1551024709-8f23befc6f87?w=400&h=400&fit=crop", category: "Desserts" },
  ],
  "caribbean": [
    { name: "Jerk Chicken", image: "https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=400&h=400&fit=crop", category: "Non-Veg", chefPick: true },
    { name: "Plantain", image: "https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=400&h=400&fit=crop", category: "Veg" },
    { name: "Rice & Peas", image: "https://images.unsplash.com/photo-1432139555190-58524dae6a55?w=400&h=400&fit=crop", category: "Veg" },
    { name: "Curry Goat", image: "https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=400&h=400&fit=crop", category: "Non-Veg", chefPick: true },
    { name: "Rum Cake", image: "https://images.unsplash.com/photo-1551024709-8f23befc6f87?w=400&h=400&fit=crop", category: "Desserts" },
  ],
  "mediterranean": [
    { name: "Hummus Platter", image: "https://images.unsplash.com/photo-1540914124281-342587941389?w=400&h=400&fit=crop", category: "Veg", chefPick: true },
    { name: "Lamb Kebabs", image: "https://images.unsplash.com/photo-1547573854-74d2a71d0826?w=400&h=400&fit=crop", category: "Non-Veg" },
    { name: "Falafel", image: "https://images.unsplash.com/photo-1529006557810-274b9b2fc783?w=400&h=400&fit=crop", category: "Veg" },
    { name: "Grilled Sea Bass", image: "https://images.unsplash.com/photo-1432139555190-58524dae6a55?w=400&h=400&fit=crop", category: "Non-Veg", chefPick: true },
    { name: "Baklava", image: "https://images.unsplash.com/photo-1551024709-8f23befc6f87?w=400&h=400&fit=crop", category: "Desserts", chefPick: true },
  ],
};

const findMatchingDishes = (name: string): DishItem[] => {
  for (const [key, dishes] of Object.entries(MENU_DISHES)) {
    if (name.includes(key)) return dishes;
  }
  return [];
};

export const getMenuDishes = (name: string): DishItem[] => {
  if (!name) return [];
  const lower = name.toLowerCase();
  if (MENU_DISHES[lower]) return MENU_DISHES[lower];
  return findMatchingDishes(lower);
};

/* ── Menu Dropdown Thumbnails ── */
export const MENU_THUMBS: Record<string, string> = {
  "south indian": "https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=80&h=60&fit=crop",
  "north indian": "https://images.unsplash.com/photo-1596797038530-2c107229654b?w=80&h=60&fit=crop",
  "sri lankan": "https://images.unsplash.com/photo-1625398407796-82650a8c135f?w=80&h=60&fit=crop",
  "tamil": "https://images.unsplash.com/photo-1630383249896-424e482df921?w=80&h=60&fit=crop",
  "western": "https://images.unsplash.com/photo-1544025162-d76694265947?w=80&h=60&fit=crop",
  "chinese": "https://images.unsplash.com/photo-1563245372-f21724e3856d?w=80&h=60&fit=crop",
  "thai": "https://images.unsplash.com/photo-1562565652-a0d8f0c59eb4?w=80&h=60&fit=crop",
  "caribbean": "https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=80&h=60&fit=crop",
  "mediterranean": "https://images.unsplash.com/photo-1540914124281-342587941389?w=80&h=60&fit=crop",
  "vegetarian": "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=80&h=60&fit=crop",
  "international": "https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=80&h=60&fit=crop",
};

export const getMenuThumb = (name: string): string | null => {
  if (!name) return null;
  const lower = name.toLowerCase();
  for (const [key, url] of Object.entries(MENU_THUMBS)) {
    if (lower.includes(key)) return url;
  }
  return "https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=80&h=60&fit=crop";
};
